import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useToast } from '../context/ToastContext';

const CATEGORIES = ['כללי', 'איטלקי', 'בשר', 'דגים', 'קינוחים', 'בצקים', 'בישולים'];

const SharedRecipeScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user] = useAuthState(auth);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('איטלקי');
  const { showToast } = useToast();

  const handleSaveRecipe = async () => {
    if (!user || !recipe) return;
    setSaving(true);
    try {
      // Check if already saved by this user
      const recipesRef = collection(db, 'recipes');
      const q = query(recipesRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);

      let alreadySaved = false;
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.content === recipe.content) {
          alreadySaved = true;
        }
      });

      if (alreadySaved) {
        showToast('המתכון כבר שמור בספר המתכונים שלך! 🤩', 'warning');
        setSaving(false);
        return;
      }

      await addDoc(recipesRef, {
        ...recipe,
        userId: user.uid,
        category: selectedCategory,
        isShared: true,
        date: new Date().toLocaleDateString('he-IL')
      });
      
      showToast('המתכון נשמר בהצלחה בספר המתכונים שלך! 🎉', 'success');
      navigate('/');
    } catch (err) {
      console.error("Error saving recipe:", err);
      showToast('שגיאה בשמירת המתכון. נסה שוב מאוחר יותר.', 'error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const docRef = doc(db, 'recipes', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setRecipe(data);
          let match = 'איטלקי';
          if (data.category) {
            const foundCat = CATEGORIES.find(c => String(data.category).includes(c));
            if (foundCat) match = foundCat;
          }
          setSelectedCategory(match);
        } else {
          setError('המתכון לא נמצא. ייתכן שהוא נמחק או שהקישור שגוי.');
        }
      } catch (err) {
        console.error("Error fetching shared recipe:", err);
        setError('שגיאה בטעינת המתכון. ייתכן ואין לך הרשאת גישה.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRecipe();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-surface" dir="rtl">
        <div className="text-2xl text-primary font-bold animate-pulse flex items-center gap-2">
          <span className="material-symbols-outlined animate-spin text-3xl">autorenew</span>
          טוען מתכון... 🍳
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-surface" dir="rtl">
        <span className="material-symbols-outlined text-6xl text-error mb-4">sentiment_dissatisfied</span>
        <h2 className="text-3xl font-headline font-bold text-on-surface mb-2">אופס! 😕</h2>
        <p className="text-on-surface-variant font-medium mb-8 text-lg px-4 text-center">{error}</p>
        <button 
          onClick={() => navigate('/')} 
          className="px-8 py-3 bg-primary text-white font-bold rounded-xl shadow-md hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined">home</span>
          חזור לדף הבית
        </button>
      </div>
    );
  }

  return (
    <div className="bg-surface text-on-surface min-h-screen pb-32 font-body" dir="rtl">
      
      {/* ── TopAppBar ── */}
      <header className="w-full sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md border-b border-outline-variant/10">
        <div className="flex justify-between items-center px-6 py-4 max-w-7xl mx-auto">
           <div className="flex items-center gap-3">
             <button title="חזרה" onClick={() => navigate('/')} className="p-2 rounded-full hover:bg-surface-container transition-colors flex items-center justify-center">
               <span className="material-symbols-outlined text-on-surface">arrow_forward</span>
             </button>
             <span className="text-xl md:text-2xl font-headline font-extrabold tracking-tight text-primary">CookWithAI</span>
           </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-8">
        
        {/* Title and Category */}
        <section className="mb-8 flex flex-col items-start gap-4">
          <h1 className="text-3xl md:text-4xl font-headline font-extrabold tracking-tight text-on-surface">
            מתכון ששותף איתך:
          </h1>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 w-full">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-label font-semibold transition-all cursor-pointer active:scale-95 ${selectedCategory === cat ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-surface-container text-on-surface-variant'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Recipe Content Card */}
        <div className="bg-surface-container-lowest rounded-2xl p-6 md:p-8 shadow-[0px_4px_20px_rgba(0,0,0,0.05)] border border-outline-variant/15 mb-10">
          
          {recipe.originalUrl && (
            <div className="flex items-center gap-3 bg-primary/5 p-4 md:p-5 rounded-xl mb-8 border border-primary/20">
              <span className="material-symbols-outlined text-primary text-2xl">link</span>
              <div className="flex flex-col">
                <span className="text-xs text-on-surface-variant font-bold uppercase tracking-wider mb-1">מקור הסרטון</span>
                <a 
                  href={recipe.originalUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary font-bold hover:underline text-sm sm:text-base flex items-center gap-1"
                >
                  צפה בסרטון המקורי 🎬
                </a>
              </div>
            </div>
          )}

          <div className="text-on-surface text-base sm:text-lg leading-relaxed whitespace-pre-wrap word-break-break-word font-medium">
            {recipe.content}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-primary-fixed/30 p-8 rounded-2xl text-center border-2 border-primary/10 shadow-[0px_8px_24px_rgba(0,106,98,0.05)] group">
          <h3 className="text-2xl font-headline font-bold mb-3 text-on-surface">אהבת את המתכון? 😋</h3>
          
          {user ? (
            <>
              <p className="text-on-surface-variant font-medium mb-6 text-sm md:text-base">שמור אותו עכשיו בספר המתכונים האישי שלך!</p>
              <button 
                onClick={handleSaveRecipe} 
                disabled={saving}
                className={`w-full sm:w-auto px-8 py-4 ${saving ? 'bg-outline-variant cursor-not-allowed' : 'bg-primary hover:bg-primary/90'} text-white border-none rounded-xl text-lg font-bold cursor-pointer transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 mx-auto`}
              >
                {saving && <span className="material-symbols-outlined">hourglass_empty</span>}
                {saving ? 'שומר מתכון...' : 'שמור בספר המתכונים שלי 💾'}
              </button>
            </>
          ) : (
            <>
              <p className="text-on-surface-variant font-medium mb-6 text-sm md:text-base">בוא לשמור ולחלץ מתכונים משלך בעזרת בינה מלאכותית!</p>
              <button 
                onClick={() => navigate('/')} 
                className="w-full sm:w-auto px-8 py-4 bg-primary text-white font-bold rounded-xl text-lg hover:bg-primary/90 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 mx-auto"
              >
                <span className="material-symbols-outlined">rocket_launch</span>
                היכנס לאפליקציה עכשיו 🚀
              </button>
            </>
          )}
        </div>

      </main>
    </div>
  );
};

export default SharedRecipeScreen;
